import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useAuth0 } from "src/contexts/auth0Provider";
import {
  useApolloSettings,
  useTenantId,
} from "src/contexts/global/ApolloSettingsContext";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import { AIMessage } from "./AIMessage";
import { createHtmlParsingOptions } from "./HTMLElementOverrides";
import { AGENTCORE_NAMES, GUARDRAIL_REJECTION_MESSAGE } from "./consts";
import streamAIResponse from "./utils/streamAIResponse";

export const AISummaryWithAgentCore = ({
  htmlElementOverrides = {},
  sourceId,
  subjectId,
  collapsible,
  title,
  isPanelVisible,
}) => {
  const authState = useAuth0();
  const workspace = useWorkspace();
  const { id: workspaceId } = workspace;

  const [content, setContent] = useState("");
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState(null);
  const { apiUrl } = useApolloSettings();
  const tenantId = useTenantId();

  const shouldSkip = !subjectId || !sourceId;

  /**
   * Helper function to check if AI has finished responding by looking for final-response tags
   * @param {string} responseContent - Content to check for final-response tags
   * @returns {boolean} True if final-response tag is found
   */
  const hasFinishedResponding = useCallback((responseContent) => {
    if (!responseContent || typeof responseContent !== "string") {
      return false;
    }
    return (
      responseContent.includes("<final-response>") &&
      responseContent.includes("</final-response>")
    );
  }, []);

  const createAISummary = useCallback(async () => {
    if (shouldSkip) {
      return;
    }

    try {
      setComplete(false);
      setError(null);
      setContent("");

      const token = await authState.getTokenSilently();

      await streamAIResponse(
        {
          sourceId,
          teamId: subjectId,
        },
        {
          tenantId,
          apiUrl,
          agentName: AGENTCORE_NAMES.SUMMARIES,
          headers: {
            Authorization: `Bearer ${token}`,
            "x-workspace-id": workspaceId,
          },
          onError: (err) => {
            setError(err);
          },
          onMessageChunk: (message) => {
            setContent(message?.content || "");
          },
          onMessage: (finalMessage) => {
            setContent((prev) => {
              const newContent = prev + (finalMessage?.content || "");
              setComplete(hasFinishedResponding(newContent));
              return newContent;
            });
          },
          onDone: () => {
            setComplete(true);
          },
        }
      );
    } catch (e) {
      setError(e);
    }
  }, [
    shouldSkip,
    authState,
    sourceId,
    subjectId,
    tenantId,
    apiUrl,
    workspaceId,
    hasFinishedResponding,
  ]);

  // Reload AI summary when subjectId changes AND panel is visible
  // Only refresh when panel is actually visible (not loading in background)
  useEffect(() => {
    if (subjectId && isPanelVisible) {
      // Clear previous content and error when subjectId changes
      setContent("");
      setComplete(false);
      setError(null);
      // Reload summary for new subject - only if panel is visible
      const shouldAutoLoad = !collapsible || collapsible === "expanded";
      if (shouldAutoLoad) {
        createAISummary();
      }
    }
  }, [subjectId, isPanelVisible, collapsible, createAISummary]);

  // Auto-load data when component starts in expanded state or when panel becomes visible
  useEffect(() => {
    const shouldAutoLoad = !collapsible || collapsible === "expanded";
    const isPanelVisibleOrUndefined =
      isPanelVisible === undefined || isPanelVisible;

    if (shouldAutoLoad && isPanelVisibleOrUndefined && !content && !error) {
      createAISummary();
    }
  }, [collapsible, isPanelVisible, content, error, createAISummary]);

  const options = createHtmlParsingOptions(htmlElementOverrides);

  if (shouldSkip) {
    return null;
  }

  return (
    !content.includes(GUARDRAIL_REJECTION_MESSAGE) && (
      <AIMessage
        content={content}
        options={options}
        complete={complete}
        collapsible={collapsible}
        onReveal={createAISummary}
        title={title}
        errorMessage={
          error ? (
            <>
              There was an error generating the AI summary
              {error.message && <div>{error.message}</div>}
            </>
          ) : undefined
        }
      />
    )
  );
};

AISummaryWithAgentCore.propTypes = {
  sourceId: PropTypes.string.isRequired,
  subjectId: PropTypes.string.isRequired,
  htmlElementOverrides: PropTypes.any,
  collapsible: PropTypes.oneOf(["collapsed", "expanded"]),
  title: PropTypes.string,
  isPanelVisible: PropTypes.bool,
};
